"use client";

import React from "react";
import { Clock, CircleQuestionMark as HelpCircle, MapPin, PhoneCall, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
}
const faqs: FaqEntry[] = [{
  id: "item-1",
  question: "What is your typical emergency response time in Jonesboro?",
  answer: "Our licensed emergency technicians typically arrive within 45 to 60 minutes for urgent calls in Jonesboro and neighboring Clayton County communities. When you call our 24/7 dispatch line, we immediately assign the closest available mobile unit equipped with full repair gear.",
  category: "Response Time"
}, {
  id: "item-2",
  question: "What areas and boundaries do your plumbers cover?",
  answer: "We proudly serve the entire City of Jonesboro and surrounding areas within Clayton, Henry, and South Fulton counties. This includes Riverdale, Morrow, Forest Park, Stockbridge, Lovejoy, and Fayetteville. If you are uncertain whether your address is covered, call us and our dispatcher will verify instantly.",
  category: "Service Area"
}, {
  id: "item-3",
  question: "Are you genuinely available 24 hours a day, including weekends and holidays?",
  answer: "Yes. Plumbing disasters do not follow regular business hours. We maintain an active emergency standby crew 24 hours a day, 7 days a week, 365 days a year—including nights, weekends, and major holidays. You will always speak with a live coordinator, never an answering machine.",
  category: "24/7 Availability"
}, {
  id: "item-4",
  question: "Which emergency plumbing services do you handle?",
  answer: "We tackle all critical plumbing failures, including burst pipes, active water leaks, severe sewer backups, overflowing toilets, malfunctioning water heaters, sudden loss of water pressure, and emergency drain clearing. Our trucks carry comprehensive tools and replacement components for immediate on-site fixes.",
  category: "Services"
}, {
  id: "item-5",
  question: "How do I request immediate emergency plumbing dispatch?",
  answer: "For active leaks or flooding, call our direct emergency line immediately. Our dispatch team will collect your address, describe emergency shut-off steps to help limit water damage while we are en route, and provide an accurate arrival estimate. You can also submit an urgent online request via our contact page.",
  category: "Dispatch"
}, {
  id: "item-6",
  question: "Do you provide upfront pricing before starting emergency repairs?",
  answer: "Yes. Before beginning any physical repair work, our technician conducts an on-site diagnosis and presents a clear, straightforward estimate with no hidden surprise fees. You review and approve the exact solution before we turn a single wrench.",
  category: "Pricing"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };
  return <section id="faqaccordion" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full border-border bg-muted/60 text-foreground">
            <HelpCircle className="size-3.5 text-primary" />
            Clear Answers &amp; Transparent Service
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Get fast answers about our Jonesboro service area, response windows, emergency procedures, and upfront service standards.
          </p>
        </div>

        {/* FAQ & Quick Contact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Accordion Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="lg:col-span-8 bg-card border border-border rounded-xl shadow-xs p-6 sm:p-8">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqs.map((faq, i) => <motion.div key={faq.id} variants={itemVariants} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border/80 rounded-lg px-4 sm:px-5 transition-colors data-[state=open]:bg-muted/30 data-[state=open]:border-primary/30">
                    <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground hover:no-underline hover:text-primary py-4">
                      <span className="flex items-center gap-3 text-left">
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                      <p>{faq.answer}</p>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>

          {/* Quick Support & Dispatch Side Card */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-primary text-primary-foreground rounded-xl p-6 sm:p-8 shadow-md flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center justify-center p-3 rounded-lg bg-primary-foreground/10 mb-5">
                  <PhoneCall className="size-6 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-bold font-default text-primary-foreground">
                  Need Immediate Help?
                </h3>
                <p className="mt-2 text-sm text-primary-foreground/80 font-default leading-relaxed">
                  Active leak, burst pipe, or sewer backup? Our live Jonesboro dispatch team is ready 24/7 to send an emergency plumber right away.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-primary-foreground/15 space-y-3">
                <a href="tel:4708479076" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-accent text-accent-foreground font-semibold px-4 py-3 text-sm shadow-xs hover:bg-accent/90 transition-colors">
                  <PhoneCall className="size-4" />
                  Call 24/7 Emergency Line
                </a>
                <Button asChild variant="outline" className="w-full bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20 hover:text-primary-foreground">
                  <Link to="/contact/">
                    Book Non-Emergency Service
                  </Link>
                </Button>
              </div>
            </div>

            {/* Service Highlights Box */}
            <div className="bg-muted/40 border border-border rounded-xl p-6 space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                Why Trust Our Team
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2.5">
                  <Clock className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Rapid 45-60 minute local response</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Locally operated across Clayton County</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Wrench className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Licensed, insured master technicians</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}